import React from 'react';
import { 
  Shield, 
  Zap, 
  Users, 
  Globe, 
  Lock, 
  TrendingUp,
  BadgeCheck,
  Sparkles,
  Clock,
  HeartHandshake,
  CheckCircle2,
  XCircle,
  Award
} from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';

const WhyChooseUs = () => {
  const { t } = useLanguage();
  
  const advantages = [
    {
      icon: Shield,
      title: t('donorProtection'),
      description: t('donorProtectionDesc'),
      color: 'from-emerald-500 to-teal-500',
      stat: 'Verified',
      statLabel: t('campaigns')
    },
    {
      icon: Lock,
      title: t('securePayments'),
      description: t('securePaymentsDesc'),
      color: 'from-blue-500 to-indigo-500',
      stat: '256-bit',
      statLabel: 'SSL'
    },
    {
      icon: BadgeCheck,
      title: t('verifiedCampaigns'),
      description: t('verifiedCampaignsDesc'),
      color: 'from-emerald-500 to-teal-500',
      stat: '3-Step',
      statLabel: t('verification')
    },
    {
      icon: Sparkles,
      title: t('aiPoweredTools'),
      description: t('aiPoweredToolsDesc'),
      color: 'from-emerald-500 to-blue-500',
      stat: '8+',
      statLabel: t('aiFeatures')
    },
    {
      icon: Globe,
      title: t('multiLanguageSupport'),
      description: t('multiLanguageSupportDesc'),
      color: 'from-orange-500 to-red-500',
      stat: '8+',
      statLabel: t('languages')
    },
    {
      icon: Clock,
      title: t('fastFundRelease'),
      description: t('fastFundReleaseDesc'),
      color: 'from-green-500 to-emerald-500',
      stat: '24-48h',
      statLabel: t('releaseTime')
    }
  ];

  const comparisons = [
    {
      feature: t('donorProtection'),
      us: true,
      others: false
    },
    {
      feature: t('securePayments'),
      us: true,
      others: 'partial'
    },
    {
      feature: t('aiPoweredTools'),
      us: true,
      others: false
    },
    {
      feature: t('verifiedCampaigns'),
      us: true,
      others: 'partial'
    },
    {
      feature: t('multiLanguageSupport'),
      us: true,
      others: false
    },
    {
      feature: t('fastFundRelease'),
      us: true,
      others: false
    },
    {
      feature: t('transparentFees'),
      us: true,
      others: false
    },
    {
      feature: t('trustScore'),
      us: true,
      others: false
    }
  ];

  return (
    <section className="py-12 bg-secondary/50 relative overflow-hidden">
      {/* Background decoration */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-20 left-10 w-64 h-64 bg-emerald-500/10 rounded-full blur-3xl" />
        <div className="absolute bottom-20 right-10 w-64 h-64 bg-teal-500/10 rounded-full blur-3xl" />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center px-4 py-2 bg-emerald-500/20 rounded-full mb-4">
            <Award className="w-4 h-4 text-emerald-400 mr-2" />
            <span className="text-emerald-400 text-sm font-medium">
              {t('whyChooseUs')}
            </span>
          </div>
          <h2 className="text-3xl sm:text-4xl font-bold text-foreground mb-4">
            {t('mostTrustedPlatform')}
          </h2>
          <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
            {t('whyChooseSubtitle')}
          </p>
        </div>

        {/* Advantages Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-20">
          {advantages.map((advantage, index) => (
            <div
              key={index}
              className="group bg-card/80 backdrop-blur-sm border border-border rounded-2xl p-6 hover:border-emerald-500/50 transition-all duration-300 hover:shadow-xl hover:shadow-emerald-500/10"
            >
              {/* Icon & Stat */}
              <div className="flex items-start justify-between mb-4">
                <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${advantage.color} flex items-center justify-center group-hover:scale-110 transition-transform`}>
                  <advantage.icon className="w-6 h-6 text-white" />
                </div>
                <div className="text-right">
                  <p className="text-2xl font-bold text-foreground">{advantage.stat}</p>
                  <p className="text-xs text-muted-foreground">{advantage.statLabel}</p>
                </div>
              </div>
              
              {/* Content */}
              <h3 className="text-lg font-semibold text-foreground mb-2 group-hover:text-emerald-400 transition-colors">
                {advantage.title}
              </h3>
              <p className="text-muted-foreground text-sm leading-relaxed">
                {advantage.description}
              </p>
            </div>
          ))}
        </div>

        {/* Comparison Table */}
        <div className="bg-card/80 backdrop-blur-sm border border-border rounded-2xl p-8">
          <h3 className="text-2xl font-bold text-foreground text-center mb-8">
            {t('howWeCompare')}
          </h3>
          
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-border">
                  <th className="text-left py-4 px-4 text-muted-foreground font-medium">
                    {t('feature')}
                  </th>
                  <th className="text-center py-4 px-4">
                    <div className="inline-flex items-center gap-2 bg-gradient-to-r from-emerald-500 to-teal-600 text-white px-4 py-2 rounded-full">
                      <HeartHandshake className="w-4 h-4" />
                      <span className="font-bold">FundTheHope</span>
                    </div>
                  </th>
                  <th className="text-center py-4 px-4 text-muted-foreground">
                    {t('otherPlatforms')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {comparisons.map((item, index) => (
                  <tr key={index} className="border-b border-border/50 hover:bg-secondary/30 transition-colors">
                    <td className="py-4 px-4 text-foreground">{item.feature}</td>
                    <td className="py-4 px-4 text-center">
                      <CheckCircle2 className="w-6 h-6 text-emerald-500 mx-auto" />
                    </td>
                    <td className="py-4 px-4 text-center">
                      {item.others === true ? (
                        <CheckCircle2 className="w-6 h-6 text-emerald-500 mx-auto" />
                      ) : item.others === 'partial' ? (
                        <span className="text-yellow-500 text-sm font-medium">Partial</span>
                      ) : (
                        <XCircle className="w-6 h-6 text-red-500 mx-auto" />
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

      </div>
    </section>
  );
};

export default WhyChooseUs;
